import { type AuthMe, consumeOrgHint, fetchAuthMe, loginRedirect, orgFromPath } from '@entailor/auth-kit-browser';
import * as Sentry from '@sentry/react';
import { type ReactNode, useEffect, useState } from 'react';

import { getSessionBySlug, replaceSessions, resolveLandingOrgSlug, setActiveOrg } from 'auth/sessionStore';

// Establishes the platform session before anything else mounts: loads /_auth/me,
// caches the org list, and settles which org this page is acting as. There is no
// logged-out state to render — no session means a redirect to the AS.
//
// The org comes from the URL (/o/<slug>/...). A URL without one is redirected to
// the resolved landing org rather than guessed at, so the address bar always says
// which org the page is acting as and a link is always shareable.
//
// Its own bare markup rather than LoadingScreen/ErrorScreen: those render Page,
// which renders Header, which links — and there is no router yet to link within.

type State =
  { status: 'loading' } | { status: 'redirecting' } | { status: 'error' } | { status: 'ready'; orgSlug: string };

interface Props {
  children: (orgSlug: string) => ReactNode;
}

export default function AuthGuard({ children }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    async function checkSession() {
      let me: AuthMe | null;
      try {
        me = await fetchAuthMe();
      } catch (error) {
        Sentry.captureException(error);
        setState({ status: 'error' });
        return;
      }

      if (!me || me.orgs.length === 0) {
        setState({ status: 'redirecting' });
        loginRedirect();
        return;
      }

      replaceSessions(me);

      const fromPath = orgFromPath();
      if (fromPath) {
        const session = getSessionBySlug(fromPath);
        if (!session) {
          // Not an org this session holds. Log in for it: the platform adds the
          // slot if the user is entitled, and the AS stops them there if not.
          setState({ status: 'redirecting' });
          loginRedirect({ org: fromPath });
          return;
        }
        setActiveOrg(session.orgSlug, session.orgId);
        setState({ status: 'ready', orgSlug: session.orgSlug });
        return;
      }

      const slug = resolveLandingOrgSlug(consumeOrgHint());
      const session = slug ? getSessionBySlug(slug) : null;
      if (!slug || !session) {
        setState({ status: 'redirecting' });
        loginRedirect();
        return;
      }
      // Replace rather than push: a bare URL is not a history entry worth keeping,
      // and Back should leave the app rather than bounce through this redirect.
      // Carry the whole URL across, hash included — a shared deep link keeps its anchor.
      const { pathname, search, hash } = window.location;
      window.history.replaceState(null, '', `/o/${slug}${pathname}${search}${hash}`);
      setActiveOrg(session.orgSlug, session.orgId);
      setState({ status: 'ready', orgSlug: session.orgSlug });
    }

    void checkSession();
  }, []);

  if (state.status === 'ready') return <>{children(state.orgSlug)}</>;

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
      }}
    >
      {state.status === 'error' ? 'Could not load your session. Please reload.' : null}
      {state.status === 'redirecting' ? 'Signing in' : null}
      {state.status === 'loading' ? 'Authenticating' : null}
    </div>
  );
}
